// ==================== FILTER TYPES ====================

export interface JuryFilters {
  academicYearId?: string;
  filiereId?: string;
  niveau?: number;
  campusId?: string;
}

export interface GradeTrackingFilters {
  academicYearId?: string;
  filiereIds?: string[];
  niveaux?: number[];
  campusIds?: string[];
}

export interface JuryPvSignature {
  userId: string;
  name: string;
  role: string;
  signedAt: string;
}

export interface JuryFiliereOption {
  id: string;
  code: string;
  name: string;
  optionName: string | null;
  /** Semester where the option branches off the tronc commun (null = unknown) */
  specialisationStartSemester: number | null;
}

export interface JuryFiltersResponse {
  academicYears: { id: string; name: string; isCurrent: boolean }[];
  filieres: {
    id: string;
    code: string;
    name: string;
    nombreAnnees: number;
    options: JuryFiliereOption[];
  }[];
  niveaux: number[];
  campuses: { id: string; name: string }[];
}

// ==================== GRADE TRACKING (SUIVI DES NOTES) ====================

export interface GradeTrackingItem {
  semesterNumber: number;
  moduleId: string;
  moduleName: string;
  moduleCode: string;
  moduleElementId: string;
  moduleElementName: string;
  professorName: string | null;
  isGraded: boolean;
  isFinalized: boolean;
  gradedStudentCount: number;
  totalStudentCount: number;
}

export interface GradeTrackingReport {
  totalMatieres: number;
  gradedMatieres: number;
  ungradedMatieres: number;
  finalizationRate: number;
  items: GradeTrackingItem[];
}

// ==================== JURY PV DATA ====================

export interface JuryPvModuleElement {
  id: string;
  name: string;
  weight: number;
}

export interface JuryPvModule {
  id: string;
  name: string;
  code: string;
  coefficient: number;
  elements: JuryPvModuleElement[];
}

export interface JuryPvSemester {
  number: number;
  name: string;
  modules: JuryPvModule[];
}

export interface JuryStudentRow {
  studentId: string;
  studentCode: string;
  studentName: string;
  groupeName: string;
  grades: Record<string, number | null>;
  moduleAverages: Record<string, number | null>;
  semesterAverages: Record<string, number | null>;
  annualAverage: number | null;
  decisionPreJury: string | null;
  decisionOfficiel: string | null;
  mention: string | null;
}

export interface JuryPvData {
  id: string;
  pvReference: string | null;
  academicYearName: string;
  filiereName: string;
  filiereCode: string;
  campusName: string;
  diplome: string;
  optionName: string | null;
  niveau: number;
  isFinalYear: boolean;
  /** True once the Directeur Pédagogique has signed: only the DP can still edit. */
  isLocked: boolean;
  semesters: JuryPvSemester[];
  students: JuryStudentRow[];
  signedByName: string | null;
  signedByRole: string | null;
  signedAt: string | null;
  signatures: JuryPvSignature[];
  createdBy: string;
  createdAt: string;
  lastModifiedBy: string | null;
  lastModifiedAt: string | null;
}

// ==================== DECISIONS ====================

export const PRE_JURY_DECISIONS = [
  'Admis',
  'Admis par compensation',
  'Admis par délibération',
  'Ajourné',
  'Redoublant',
  'Exclu',
  'Abandon',
  'Diplômé',
] as const;
export type PreJuryDecision = (typeof PRE_JURY_DECISIONS)[number];

export const OFFICIEL_DECISIONS = [
  'Admis',
  'Admis par compensation',
  'Admis par délibération',
  'Ajourné',
  'Redoublant',
  'Exclu',
  'Abandon',
  'Diplômé',
] as const;
export type OfficielDecision = (typeof OFFICIEL_DECISIONS)[number];

export const MENTIONS = ['Passable', 'Assez Bien', 'Bien', 'Très Bien'] as const;
export type Mention = (typeof MENTIONS)[number];

// ==================== UPDATE DTO ====================

export interface StudentDecisionUpdate {
  studentId: string;
  decisionPreJury?: string;
  decisionOfficiel?: string;
  mention?: string;
}


// ==================== PASSAGE D'ANNÉE ====================

/** Outcome tally of a "passage d'année" run over one PV. */
export interface ProgressionSummary {
  total: number;
  promoted: number;
  graduated: number;
  repeated: number;
  archived: number;
  unchanged: number;
  withoutDecision: number;
}
